import { QuestionMarkCircleIcon } from '@heroicons/react/24/solid';
import React, { FunctionComponent, useState } from 'react';
import { sanitizeKeyword } from '../../../../services/briefs';
import { BriefKeywordInput } from '../../../../services/models/brief-creation-request';
import { BriefKeywordType } from '../../../../services/models/brief-keyword-type';
import HelpDialog from '../../../help-dialog/HelpDialog';
import { TagInput } from '../../../ui';
import { getExactMatchTagClassName, sameTag } from '../../../ui/TagInput';

export interface CreateBriefKeywordProps {
  title?: string;
  keywords: BriefKeywordInput[];
  error?: string;
  maxItems?: number;
  placeholder?: string;
  update: (keywords: BriefKeywordInput[]) => void;
}

/**
 * Onboarding topic editor: chips are held client-side as BriefKeywordInput
 * (a leading quote marks an exact-match keyword) until the wizard creates
 * the brief.
 */
const CreateBriefKeyword: FunctionComponent<CreateBriefKeywordProps> = ({
  title,
  maxItems,
  placeholder,
  keywords,
  error,
  update,
}) => {
  const [isExactMatchDraft, setIsExactMatchDraft] = useState(false);
  const [showHelpDialog, setShowHelpDialog] = useState(false);

  const handleCloseHelpDialog = () => setShowHelpDialog(false);
  const handleShowHelpDialog = () => setShowHelpDialog(true);

  const values = (keywords || []).map(x => x.keyword);

  const toKeywordInputs = (next: string[]) => {
    const result: BriefKeywordInput[] = [];

    for (const tag of next) {
      const existing = (keywords || []).find(x => x.keyword === tag);

      if (existing) {
        result.push(existing);
        continue;
      }

      // New tags arrive raw from the input; the stored keyword loses its
      // quotes, so dedupe on the sanitized form ("ai" vs ai is a duplicate
      // TagInput cannot see).
      const keyword = sanitizeKeyword(tag).trim();

      if (keyword.length > 0 && !result.some(x => sameTag(x.keyword, keyword))) {
        result.push({
          type: BriefKeywordType.Topic,
          exactMatch: tag.startsWith('"'),
          keyword,
        });
      }
    }

    return result;
  };

  const handleChange = (next: string[]) => {
    const result = toKeywordInputs(next);

    // Every new tag can dedupe away (e.g. a quoted variant of an existing
    // keyword) — don't bother the parent with a no-op update.
    const unchanged =
      result.length === (keywords || []).length && result.every((x, i) => x === keywords[i]);

    if (!unchanged) {
      update(result);
    }
  };

  return (
    <div className="CreateBriefKeyword">
      {title && <div className="block font-bold mb-1">{title}</div>}
      <p className="mt-[5px] mb-[10px] block text-[13px] text-gray-500">
        Press{' '}
        <span className="rounded-full border border-gray-500 bg-gray-100 px-2.5 py-0.5 text-[12px] font-bold">
          ENTER
        </span>{' '}
        after EACH word.
        <QuestionMarkCircleIcon
          className="inline-block h-4 w-4 ml-1 align-text-bottom text-gray-400 cursor-pointer"
          aria-hidden="true"
          onClick={handleShowHelpDialog}
        />
      </p>
      <TagInput
        values={values}
        placeholder={placeholder || 'Add keyword'}
        maxLength={52}
        // The raw batch can shrink after sanitizing (quoted variants of an
        // existing keyword dedupe away), so gate on the committed count, not
        // on the raw tag count TagInput reports.
        onAdd={next => toKeywordInputs(next).length <= (maxItems || 20)}
        onChange={handleChange}
        onDraftChange={draft => setIsExactMatchDraft(draft.startsWith('"'))}
        getTagClassName={tag => getExactMatchTagClassName(keywords, tag)}
        className={isExactMatchDraft ? 'border-secondary ring-1 ring-secondary' : ''}
      />
      <div className="flex justify-between pt-1">
        <div className="text-gray-400">
          [{values.length}/{maxItems}] keywords / phrase
        </div>
        {isExactMatchDraft && (
          <span className="rounded-sm bg-secondary px-2 py-0.5 text-xs font-medium text-white">
            Entering an Exact Match
          </span>
        )}
      </div>
      {!!error && <p className="mt-1 text-sm text-red-600">{error}</p>}

      <HelpDialog show={showHelpDialog} onHide={handleCloseHelpDialog}>
        <p>
          Enter some keywords to start your News Brief. Keep your Brief focused by using related
          terms, ie: machine learning, AI.
        </p>
        <p>
          Press <code>ENTER</code> after each one.
        </p>
      </HelpDialog>
    </div>
  );
};

export default CreateBriefKeyword;
